import { LanguagesEnum } from '@/api/enums';
import { equal, handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import {
  AdminTask,
  AdminTaskPayload,
  AdminTaskVisibilityGroupCondition,
  EditableTaskFormState,
} from '@/client/admin-tasks';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { DialogContext } from '@/common/context/ConfirmationDialog';
import { LanguageContext } from '@/common/context/LanguageContext';
import { TranslationFunctionType } from '@/common/types';
import { SortableTaskItem } from '@/components/admin-tasks';
import { adminTaskSchema } from '@/components/admin-tasks/validations/admin-task';
import {
  useAdminTasks,
  useCreateAdminTask,
  useDeleteAdminTask,
  useUpdateAdminTask,
} from '@/hooks/query/admin-tasks.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { formStateToAdminTask, taskToInitialValues } from '@/utils/helpers';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabMenu } from 'primereact/tabmenu';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const pathItems = (account: Account, t: TranslationFunctionType) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('admin.tasks.title'),
    url: RedirectPaths[RedirectPathsEnum.ADMIN_TASKS](),
    template: AppBreadCrumbTemplate,
  },
];

const StyledContainer = styled(FlexContainer)`
  flex: 1;
  min-height: 0;
  width: 100%;
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding-block: var(--big-padding);
`;

const PageHeader = styled(FlexContainer)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--default-padding);
`;

const PageTitle = styled.h1`
  font-size: var(--heading-medium-font-size);
  font-weight: 700;
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-size: var(--heading-small-font-size);
  line-height: var(--default-line-height);
  font-weight: 700;
  margin: 0;
  padding-left: var(--big-padding);
`;

const PageSubtitleDescription = styled.p`
  font-size: var(--small-font-size);
  line-height: var(--default-line-height);
  margin: 0;
  padding-left: var(--big-padding);
`;

const StyledTabMenu = styled(TabMenu)`
  margin-bottom: var(--default-padding);

  .p-tabmenuitem-link {
    text-transform: uppercase;
    font-weight: 600;
  }
`;

const AddTaskButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% - 2 * var(--big-padding));
  margin-inline: var(--big-padding);
  gap: var(--small-padding);
  padding: var(--big-padding);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  color: var(--gray-darker);
  font-size: var(--small-font-size);
  font-weight: 500;
  cursor: pointer;
  background: transparent;

  i {
    color: var(--black-main);
  }
`;

export const AdminTasksPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { language } = useContext(LanguageContext);
  const { setDialogData } = useContext(DialogContext);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [isForFreeTrial, setIsForFreeTrial] = useState(false);

  const { adminTasks, isLoading, refetch } = useAdminTasks({
    filters: [equal('isForFreeTrial', isForFreeTrial)],
    sort: [['order', 'asc'].join(',')],
  });

  const [orderedTasks, setOrderedTasks] = useState<AdminTask[]>([]);
  const [taskFormStates, setTaskFormStates] = useState<
    Record<string, EditableTaskFormState>
  >({});
  const initialTaskStatesRef = useRef<Record<string, EditableTaskFormState>>(
    {},
  );
  const hasSeededRef = useRef(false);
  const movingTasksRef = useRef<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const hasDirtyTasks = useMemo(
    () =>
      Object.entries(taskFormStates).some(([taskId, current]) => {
        const initial = initialTaskStatesRef.current[taskId];
        return !initial || !isEqual(initial, current);
      }),
    [taskFormStates],
  );

  useEffect(() => {
    setOrderedTasks([]);
    setTaskFormStates({});
    initialTaskStatesRef.current = {};
    hasSeededRef.current = false;
  }, [isForFreeTrial]);

  useEffect(() => {
    const tasks = adminTasks?.result ?? [];
    if (tasks.length === 0) return;

    setOrderedTasks(tasks);

    setTaskFormStates((prev) => {
      const next = { ...prev };
      tasks.forEach((task) => {
        const id = task.id as string;
        next[id] =
          prev[id] ?? taskToInitialValues(task, language as LanguagesEnum);
      });
      return next;
    });

    if (!hasSeededRef.current) {
      tasks.forEach((task) => {
        initialTaskStatesRef.current[task.id as string] = taskToInitialValues(
          task,
          language as LanguagesEnum,
        );
      });
      hasSeededRef.current = true;
    }
  }, [adminTasks?.result, language]);

  const displayTasks =
    orderedTasks.length > 0 ? orderedTasks : adminTasks?.result ?? [];
  const orderedFormValues = useMemo(
    () =>
      displayTasks.map(
        (task) =>
          taskFormStates[task.id as string] ??
          taskToInitialValues(task, language as LanguagesEnum),
      ),
    [displayTasks, taskFormStates, language],
  );

  const { create } = useCreateAdminTask();
  const { update } = useUpdateAdminTask();
  const { delete: deleteTask } = useDeleteAdminTask();

  const removeTaskFromCurrentTab = useCallback((taskId: string) => {
    setOrderedTasks((prev) => prev.filter((t) => t.id !== taskId));
    setTaskFormStates((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    delete initialTaskStatesRef.current[taskId];
  }, []);

  const saveAndMoveTask = useCallback(
    async (values: EditableTaskFormState) => {
      if (movingTasksRef.current.has(values.taskId)) return;
      movingTasksRef.current.add(values.taskId);

      try {
        const schema = adminTaskSchema(t);
        const isValid = await schema.isValid(values);
        if (!isValid) {
          toast?.error(t('toast.error'), t('admin.tasks.form.validationError'));
          return;
        }

        const payload = formStateToAdminTask(values);
        if (values.taskId.startsWith('temp-')) {
          await create(payload);
        } else {
          await update({ taskId: values.taskId, ...payload });
        }
        removeTaskFromCurrentTab(values.taskId);
        toast?.info(
          t('toast.success'),
          t('admin.tasks.management.movedToOtherTab'),
        );
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      } finally {
        movingTasksRef.current.delete(values.taskId);
      }
    },
    [create, update, removeTaskFromCurrentTab, toast, t],
  );

  const handleValuesChange = useCallback(
    (values: EditableTaskFormState) => {
      setTaskFormStates((prev) => {
        if (isEqual(prev[values.taskId], values)) {
          return prev;
        }
        return { ...prev, [values.taskId]: values };
      });

      if (values.isForFreeTrial !== isForFreeTrial) {
        saveAndMoveTask(values);
      }
    },
    [isForFreeTrial, saveAndMoveTask],
  );

  const tabMenuItems: MenuItem[] = useMemo(
    () => [
      { label: t('admin.tasks.management.tabs.payingCustomers') },
      { label: t('admin.tasks.management.tabs.freeTrials') },
    ],
    [t],
  );

  const handleTabChange = useCallback(
    (newIndex: number) => {
      const newIsForFreeTrial = newIndex === 1;
      if (newIsForFreeTrial === isForFreeTrial) return;

      if (hasDirtyTasks) {
        setDialogData({
          type: DialogType.CONFIRMATION,
          show: true,
          header: t('dialog.unsavedChanges'),
          message: t('dialog.unsavedChanges.confirm'),
          onAccept: () => setIsForFreeTrial(newIsForFreeTrial),
        });
        return;
      }

      setIsForFreeTrial(newIsForFreeTrial);
    },
    [isForFreeTrial, hasDirtyTasks, setDialogData, t],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const currentList = displayTasks;
    const oldIndex = currentList.findIndex((task) => task.id === active.id);
    const newIndex = currentList.findIndex((task) => task.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const updatedList = arrayMove(currentList, oldIndex, newIndex);
      setOrderedTasks(updatedList);

      setTaskFormStates((prev) => {
        const next = { ...prev };

        updatedList.forEach((task, index) => {
          const id = task.id as string;
          const existing =
            next[id] ?? taskToInitialValues(task, language as LanguagesEnum);

          if (existing.order !== index) {
            next[id] = {
              ...existing,
              order: index,
            };
          }
        });

        return next;
      });
    }
  };

  const executeDelete = async (values: EditableTaskFormState) => {
    if (values.taskId.startsWith('temp-')) {
      removeTaskFromCurrentTab(values.taskId);
      return;
    }

    try {
      await deleteTask({
        taskId: values.taskId,
      });
      hasSeededRef.current = false;
      await refetch();
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleDelete = (values: EditableTaskFormState) => {
    if (!values.taskId) return;

    if (values.taskId.startsWith('temp-')) {
      executeDelete(values);
      return;
    }

    setDialogData({
      type: DialogType.WARNING,
      show: true,
      showDialogConfirmation: false,
      header: t('admin.tasks.management.deleteConfirm.title'),
      customContent: (
        <div className="line-height-3">
          <p className="m-0">
            {t('admin.tasks.management.deleteConfirm.message')}{' '}
            <strong>
              {t('admin.tasks.management.deleteConfirm.messageBold')}
            </strong>
          </p>
          <p className="m-0 mt-3">
            {t('admin.tasks.management.deleteConfirm.messageExtra')}
          </p>
        </div>
      ),
      confirmButtonLabel: t('admin.tasks.management.deleteConfirm.confirm'),
      onAccept: () => executeDelete(values),
    });
  };

  const executeSave = async () => {
    setIsSaving(true);
    try {
      const orderByTaskId: Record<string, number> = {};
      displayTasks.forEach((task, index) => {
        if (task.id) {
          orderByTaskId[task.id as string] = index;
        }
      });

      const dirtyTasks = Object.entries(taskFormStates).filter(
        ([taskId, current]) => {
          const initial = initialTaskStatesRef.current[taskId];
          return !initial || !isEqual(initial, current);
        },
      );

      const schema = adminTaskSchema(t);
      const fieldLabelMap: Record<string, string> = {
        title: t('admin.tasks.form.title'),
        description: t('admin.tasks.form.description'),
        buttonName: t('admin.tasks.form.buttonLabel'),
        buttonUrl: t('admin.tasks.form.buttonLink'),
        taskLanguage: t('admin.tasks.form.taskLanguage'),
      };

      const validationErrors: string[] = [];
      for (const [, task] of dirtyTasks) {
        try {
          await schema.validate(task, { abortEarly: false });
        } catch (err: any) {
          const fieldNames = (err.inner ?? [])
            .map((e: any) => fieldLabelMap[e.path] ?? e.path)
            .filter(
              (name: string, i: number, arr: string[]) =>
                arr.indexOf(name) === i,
            );
          validationErrors.push(
            t('admin.tasks.form.validationErrorWithFields', {
              task: task.title,
              fields: fieldNames.join(', '),
            }),
          );
        }
      }

      if (validationErrors.length > 0) {
        toast?.error(t('toast.error'), validationErrors.join('\n'));
        setIsSaving(false);
        return;
      }

      let failedTasks: string[] = [];

      if (dirtyTasks.length > 0) {
        const results = await Promise.allSettled(
          dirtyTasks.map(([, task]) => {
            const withOrder: EditableTaskFormState = {
              ...task,
              order: orderByTaskId[task.taskId] ?? task.order ?? 0,
            };

            const payload: AdminTaskPayload = formStateToAdminTask(withOrder);
            return task.taskId.startsWith('temp-')
              ? create(payload)
              : update({ taskId: task.taskId, ...payload });
          }),
        );

        failedTasks = results
          .map((r, i) => ({ result: r, task: dirtyTasks[i][1] }))
          .filter((r) => r.result.status === 'rejected')
          .map((r) => r.task.title);

        if (failedTasks.length > 0) {
          toast?.error(
            t('toast.error'),
            t('admin.tasks.management.partialSaveError', {
              count: failedTasks.length,
              tasks: failedTasks.join(', '),
            }),
          );
        }
      }

      hasSeededRef.current = false;
      const { data } = await refetch();

      if (data?.result) {
        const freshTasks = data.result;

        const states: Record<string, EditableTaskFormState> = {};
        freshTasks.forEach((task) => {
          states[task.id as string] = taskToInitialValues(
            task,
            language as LanguagesEnum,
          );
        });
        initialTaskStatesRef.current = states;
        setTaskFormStates(states);
        setOrderedTasks(freshTasks);
        hasSeededRef.current = true;
      }

      if (failedTasks.length === 0) {
        toast?.success(
          t('toast.success'),
          t('admin.tasks.management.saveSuccess'),
        );
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => {
    setDialogData({
      type: DialogType.WARNING,
      show: true,
      showDialogConfirmation: false,
      header: t('admin.tasks.management.saveConfirm.title'),
      customContent: (
        <p className="m-0 line-height-3">
          {t('admin.tasks.management.saveConfirm.message')}{' '}
          <strong>{t('admin.tasks.management.saveConfirm.messageBold')}</strong>{' '}
          {t('admin.tasks.management.saveConfirm.messageEnd')}
        </p>
      ),
      confirmButtonLabel: t('admin.tasks.management.saveConfirm.confirm'),
      onAccept: () => executeSave(),
    });
  };

  const handleAddTask = () => {
    const tempId = `temp-${Date.now()}`;

    const newTask: AdminTask = {
      id: tempId,
      active: true,
      isForFreeTrial,
      order: displayTasks.length,
      translations: [
        {
          language: LanguagesEnum.EN,
          name: t('admin.tasks.newTask.title'),
          description: t('admin.tasks.newTask.description'),
          buttonName: t('admin.tasks.newTask.buttonName'),
        },
      ],
      buttonUrl: '/',
      tags: [],
      visibilityGroup: {
        enabled: false,
        condition: AdminTaskVisibilityGroupCondition.AND,
      },
      visibilityRules: [],
    };

    setOrderedTasks((prev) => [...prev, newTask]);

    const formState = taskToInitialValues(newTask, language as LanguagesEnum);
    setTaskFormStates((prev) => ({ ...prev, [tempId]: formState }));
  };

  return (
    <FlexContainer direction="column" align="flex-start" minHeight="100%">
      <AppBreadCrumb model={pathItems(currentAccount as Account, t)} />
      <PageHeader>
        <PageTitle>{t('admin.tasks.management.title')}</PageTitle>
        <AppButton
          label={t('admin.tasks.management.saveButton')}
          onClick={handleSave}
          state={
            isLoading || isSaving
              ? LoadingStatuses.LOADING
              : LoadingStatuses.IDLE
          }
        />
      </PageHeader>

      <StyledTabMenu
        model={tabMenuItems}
        activeIndex={isForFreeTrial ? 1 : 0}
        onTabChange={(e) => handleTabChange(e.index)}
      />
      <StyledContainer
        id="admin-tasks-container"
        direction="column"
        align="flex-start"
        justify="flex-start"
        gap={16}
      >
        {isLoading ? (
          <FlexContainer direction="column" height="100%" className="mt-5">
            <ProgressSpinner />
            <h3>{t('content.details.loading')}</h3>
          </FlexContainer>
        ) : (
          <>
            <div>
              <PageSubtitle>
                {isForFreeTrial
                  ? t('admin.tasks.management.subtitle.freeTrials')
                  : t('admin.tasks.management.subtitle.payingCustomers')}
              </PageSubtitle>
              <PageSubtitleDescription>
                {isForFreeTrial
                  ? t('admin.tasks.management.subtitle.freeTrials.description')
                  : t(
                      'admin.tasks.management.subtitle.payingCustomers.description',
                    )}
              </PageSubtitleDescription>
            </div>

            <DndContext
              modifiers={[restrictToVerticalAxis]}
              sensors={sensors}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={displayTasks.map((task) => task.id as string)}
                strategy={verticalListSortingStrategy}
              >
                {orderedFormValues.map((formValues, index) => (
                  <SortableTaskItem
                    key={formValues.taskId}
                    initialValues={formValues}
                    onValuesChange={handleValuesChange}
                    onDelete={() => handleDelete(formValues)}
                    style={{
                      animation: `fadeInUp 0.4s ease-out ${index * 0.1}s both`,
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>

            <AddTaskButton type="button" onClick={handleAddTask}>
              <i className="pi pi-plus" />
              {t('admin.tasks.management.addTask')}
            </AddTaskButton>
          </>
        )}
      </StyledContainer>
    </FlexContainer>
  );
};
